import { toast } from "sonner";
import { partitionFilesForScheduleDrop } from "@/react/components/entityViews/files/constants/entityFileUpload";
import {
  pluralizeFile,
  uploadAndLinkEntityFiles,
  type FileEntityType,
} from "@/react/components/entityViews/files/api/files.api";

export {
  confirmScheduleFileDrop,
  type ConfirmScheduleFileDropParams,
} from "@/react/utils/scheduleFileDropConfirm";

export interface HandleScheduleEntityFilesDropParams {
  entityType: FileEntityType;
  entityId: number;
  boardId: number;
  files: FileList | File[];
}

/** Schedule drops accept media and documents in one gesture (not first-file category). */
export async function handleScheduleEntityFilesDrop({
  entityType,
  entityId,
  boardId,
  files,
}: HandleScheduleEntityFilesDropParams): Promise<void> {
  const { accepted, rejected } = partitionFilesForScheduleDrop(files);

  if (rejected.length > 0) {
    toast.error(
      rejected.length === 1
        ? "1 file isn't a supported media or document type."
        : `${rejected.length} files aren't supported media or document types.`,
    );
  }

  if (accepted.length === 0) return;

  toast.loading(`Uploading ${pluralizeFile(accepted.length)}…`, { id: "file-upload" });

  try {
    await uploadAndLinkEntityFiles({
      entityType,
      entityId,
      boardId,
      files: accepted,
    });
    toast.success(`${pluralizeFile(accepted.length)} uploaded`, { id: "file-upload" });
  } catch (error) {
    console.error("Error uploading files:", error);
    toast.error(`Failed to upload ${pluralizeFile(accepted.length)}. Please try again.`, {
      id: "file-upload",
    });
    throw error;
  }
}

/** True for OS file drags; false for Bryntum EventDrag / TaskDrag payloads. */
export function hasFilesInDragEvent(event: DragEvent): boolean {
  return event.dataTransfer?.types.includes("Files") ?? false;
}
